import { useAtomValue } from "jotai";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Desktop } from "~/components/icons/desktop";
import { AlertCircleIcon } from "~/components/ui/icons/alert-circle";
import { ExternalLinkSimpleIcon } from "~/components/ui/icons/external-link-simple";
import { InformationIcon } from "~/components/ui/icons/information";
import { SearchIcon } from "~/components/ui/icons/search";
import { Loader } from "~/components/ui/loader";
import type {
  StockAlertsSummary,
  Stock as StockProduct,
} from "~/graphql/graphql-new.gen";
import { useProductsQuery } from "~/graphql/graphql-new.gen";
import { StoreCurrency } from "~/graphql/graphql.gen";
import { useProductsUpdatedSubscription } from "~/hooks/use-products-updated-subscription";
import useTitle from "~/hooks/use-title";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { ProductExport } from "~/views/stock/product-export";
import { ProductSortDropdownMenu } from "~/views/stock/product-sort-dropdown-menu";
import { ShopifySyncInfo } from "~/views/stock/shopify-sync-info";
import { stockSortState } from "~/views/stock/stock-state";

import type { GlobalFilterState } from "./stock-product-list";
import { StockProductList } from "./stock-product-list";
import { StockSearch } from "./stock-search";

import "./stock.css";

export function Stock() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const activeSale = useAtomValue(activeSaleState);
  const brand = useAtomValue(brandState);
  const stockSort = useAtomValue(stockSortState);
  const stockSearchTerm = searchParams.get("q");

  const pageRef = useRef<HTMLDivElement>(null);

  useTitle(`${brand?.name ?? ""} ~ Stock`);

  useProductsUpdatedSubscription();

  const { data: productsData, isLoading } = useProductsQuery(
    {
      saleId: activeSale?.id ?? "",
    },
    {
      enabled: !!activeSale?.id,
      select: (data) =>
        data.products.reduce<
          {
            gtin?: string | null;
            id: string;
            image?: string | null;
            isDigital: boolean;
            productId: string;
            productName: string;
            sku?: string | null;
            stock: StockProduct;
            stockAlertsSummary: StockAlertsSummary;
            variantName?: string | null;
          }[]
        >((acc, product) => {
          const variants = product.variants.map((variant) => ({
            ...variant,
            isDigital: product.isDigital,
            productName: product.name,
            variantName: variant.title,
          }));

          acc.push(...variants);

          return acc;
        }, []),
      refetchOnWindowFocus: false,
    },
  );

  const hasProducts = productsData && productsData.length > 0;
  const isShopifySynced = brand?.shopify?.shop && brand.shopify.isStockLive;

  return (
    <main
      ref={pageRef}
      className="stock relative flex-1 overflow-y-auto focus:outline-none"
    >
      <div className="m-4 max-w-7xl md:m-10">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h2 className="text-4xl font-bold leading-[3.75rem] text-gray-900">
              {t("sidebar.stock")}
            </h2>

            {isShopifySynced && brand.shopify ? (
              <ShopifySyncInfo shopify={brand.shopify} />
            ) : null}
          </div>
          {hasProducts ? <ProductExport /> : null}
        </header>
        {hasProducts ? (
          brand?.shopify?.shop && isShopifySynced ? (
            <div className="mb-10 mt-4 flex items-center space-x-2 text-sm text-gray-700">
              <AlertCircleIcon className="size-4 shrink-0" />
              <div className="flex flex-wrap gap-1">
                {t("stock.info_shopify")}
                <a
                  href={`https://${brand.shopify.shop}`}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="flex items-center gap-0.5 font-semibold text-green-900"
                >
                  <span>{brand.shopify.shop}</span>
                  <ExternalLinkSimpleIcon className="size-4" />
                </a>
              </div>
            </div>
          ) : (
            <p className="mb-10 mt-4 flex items-center space-x-2 text-sm text-gray-700">
              <InformationIcon className="size-4 shrink-0" />
              <span>{t("stock.info")}</span>
            </p>
          )
        ) : (
          <div className="mt-10"></div>
        )}

        <section>
          {isLoading ? (
            <div className="mb-8 flex justify-center">
              <Loader className="size-8" />
            </div>
          ) : !hasProducts ? (
            <div className="flex max-w-[55rem] flex-col justify-between space-x-6 rounded border border-gray-200 bg-gray-50 p-6 md:flex-row">
              <div className="flex w-full items-center space-x-4 pb-4 md:pb-0">
                <Desktop className="size-10" />

                <div className="flex w-full flex-col gap-1">
                  <div className="text-sm font-semibold text-gray-700">
                    {t("stock.empty_title")}
                  </div>
                  <p className="text-sm text-gray-700">
                    {t("stock.empty_text")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="relative rounded-md text-gray-500 transition-all focus-within:text-gray-900">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <SearchIcon className="size-4" />
                  </div>
                  <StockSearch />
                </div>
              </div>
              <ProductSortDropdownMenu className="min-[1200px]:hidden" />

              <StockProductList
                canEdit={!isShopifySynced}
                data={productsData.map((variant) => ({
                  id: variant.id,
                  image: variant.image,
                  isDigital: variant.isDigital,
                  productName: variant.productName,
                  variantName: variant.variantName,
                  sku: variant.sku,
                  gtin: variant.gtin,
                  totalAlertsCount:
                    variant.stockAlertsSummary.activeAlertsCount +
                    variant.stockAlertsSummary.archivedAlertsCount,
                  potentialRevenue: variant.stockAlertsSummary.potentialRevenue,
                  activeAlertsCount:
                    variant.stockAlertsSummary.activeAlertsCount,
                  stockQuantity: variant.stock.quantity,
                  stockId: variant.stock.id,
                  currency: activeSale?.currency ?? StoreCurrency.Eur,
                }))}
                state={{
                  globalFilter: {
                    term: stockSearchTerm,
                  } as GlobalFilterState,
                  sorting: stockSort,
                }}
                pageRef={pageRef}
              />
            </>
          )}
        </section>
      </div>
    </main>
  );
}
